/**
 * Input caps from docs/architecture/V1.md section 8.
 *
 * These live in shared because the parser enforces them and the editor warns
 * against them. One definition, so the two can never disagree.
 */
/** Markdown source, in bytes. Named once so the PDF text ceiling can be derived from it. */
const MAX_MARKDOWN_BYTES = 256 * 1024;
export const LIMITS = {
    /** Markdown source, in bytes. Checked before parsing. */
    MAX_MARKDOWN_BYTES,
    MAX_QUESTIONS: 200,
    /**
     * How many questions a generated practice asks for when the tutor did not
     * say.
     *
     * A default rather than a required field: a tutor photographing a worksheet
     * has an opinion about the material and usually none about the number, and
     * making them pick one before anything can run would be a form standing in
     * front of the feature. Thirty is a full practice without being a session's
     * work to review.
     *
     * The ceiling is `MAX_QUESTIONS` above, shared with the parser: a generated
     * practice and a pasted one are the same document by the time either is
     * stored, so two different caps would mean generating one the parser then
     * refuses.
     */
    DEFAULT_GENERATED_QUESTIONS: 30,
    /**
     * How many photographed pages one extraction request may carry.
     *
     * Roughly a chapter's worth of worksheet pages. The cap is not arbitrary
     * politeness: every image is decoded and re-encoded in the browser before it
     * is sent, so an accidental "select all" on a photo library is a frozen tab
     * rather than a slow one.
     *
     * Here rather than in the web app because the API bounds the same number:
     * the browser is untrusted, and two copies of this would be two answers to
     * "how many pages is too many".
     */
    MAX_EXTRACTION_IMAGES: 10,
    /**
     * The largest single image the uploader accepts, in bytes.
     *
     * Applied to the file a tutor picks, before the browser compresses it, and
     * again on the server to the decoded bytes that arrive. One number for both,
     * so the rule a tutor is told is the rule that runs.
     */
    MAX_IMAGE_BYTES: 5 * 1024 * 1024,
    /**
     * The largest one extraction request may carry, in decoded bytes, across
     * every image in it.
     *
     * Sized from what ten compressed pages actually weigh: the browser resizes
     * to 1600px and re-encodes as JPEG, which lands a dense page under 800 KB,
     * so ten of them fit here with room to spare. Ten images each just inside
     * `MAX_IMAGE_BYTES` would be fifty megabytes and is not a real upload.
     *
     * The HTTP body limit is derived from this number rather than chosen beside
     * it — see `EXTRACTION_BODY_LIMIT_BYTES` in the API, the larger of its two
     * body limits and the one only a tutor's upload may use. A batch the
     * service accepts that the body parser then rejects is a 413 nothing in the
     * application ever sees, which
     * is exactly the failure that hid behind "the reader could not be reached".
     */
    MAX_EXTRACTION_BATCH_BYTES: 8 * 1024 * 1024,
    /**
     * How many PDFs one extraction request may carry.
     *
     * Lower than the image cap, and for the opposite reason: a photograph is one
     * page, so ten of them is a short worksheet, while a PDF is a whole document
     * and five of them is already more source material than a thirty-question
     * practice can be written from. The bound that actually bites is
     * `MAX_MARKDOWN_BYTES` on the combined text.
     */
    MAX_EXTRACTION_PDFS: 5,
    /**
     * The largest single PDF the uploader accepts, in bytes.
     *
     * The same five megabytes an image gets, so a tutor is told one number about
     * one upload. Far above what a text PDF weighs — a fifty-page worksheet of
     * selectable text is a few hundred kilobytes — because the files that
     * approach this are the ones full of scans, and those are refused later for
     * having no text rather than here for being big.
     */
    MAX_PDF_BYTES: 5 * 1024 * 1024,
    /**
     * The most pages one PDF may have.
     *
     * Byte size does not bound the work of reading a PDF: pages can share one
     * compressed content stream, so a few megabytes can describe thousands of
     * pages, and every one of them is walked (SECURITY_AUDIT.md SEC-009). A
     * hundred pages is a long chapter — twice the fifty-page worksheet the size
     * limit above was written around — and far more text than one practice can
     * be generated from. The page count is read before any page is.
     */
    MAX_PDF_PAGES: 100,
    /**
     * The most text one PDF may yield before it is refused as too large.
     *
     * Reading stops here rather than after the last page. Every source
     * document is held to `MAX_MARKDOWN_BYTES` once combined, so a document
     * past four times that could never have been used, and accumulating all of
     * it first would be spending memory to arrive at a refusal.
     */
    MAX_PDF_TEXT_CHARS: 4 * MAX_MARKDOWN_BYTES,
    /**
     * The shortest extraction that counts as a document having text.
     *
     * A scanned page is not empty: pdf.js finds stray glyphs, a page number, a
     * header that happens to be real text. So "has text" cannot mean "produced
     * any characters at all", and this is where the line goes — below it, the
     * tutor is told the PDF has no readable text rather than being handed a
     * practice generated from a page number.
     *
     * Per document rather than per page: a fifty-page scan with one text page is
     * still usable material, and a one-page document with a paragraph on it is
     * the smallest thing anyone would reasonably upload.
     */
    MIN_PDF_TEXT_CHARS: 200,
    MIN_OPTIONS: 2,
    MAX_OPTIONS: 6,
    /**
     * Pairs in a MATCHING question. Its own bound rather than a reuse of the
     * option cap: a pair is two strings and a relation, and eight of them is a
     * reasonable exercise where eight options in one multiple-choice question
     * would not be a reasonable question.
     */
    MIN_PAIRS: 2,
    MAX_PAIRS: 8,
    /**
     * Words in one accepted answer to a SHORT_ANSWER (fill) question.
     *
     * A blank is filled with a word or a name — "satélite", "Mercado Central",
     * "Mercado de Cartago" — never with a sentence. The scorer compares text
     * exactly after normalization, so a longer answer is one a student will
     * phrase differently and be marked wrong for. A `$…$` mathematics span
     * counts as one word, whatever spaces are inside it.
     */
    MAX_SHORT_ANSWER_WORDS: 3,
    /**
     * The largest attempt allowance a tutor can set. Not a product rule so much
     * as a bound on a number typed into a form: past this, "how many goes does
     * this student get" is better answered by leaving it unlimited.
     */
    MAX_ATTEMPTS: 100,
    /**
     * The shortest time limit a tutor can put on a practice, in minutes.
     *
     * One, because the unit the tutor types in is minutes and there is no
     * smaller whole number. Zero is not a short practice, it is an unsittable
     * one, so it is refused rather than treated as "no limit" — there is a
     * switch for that.
     */
    MIN_PRACTICE_DURATION_MINUTES: 1,
    /**
     * The longest, in minutes. Eight hours.
     *
     * A bound on a number typed into a form rather than a product rule, like
     * `MAX_ATTEMPTS` above: past a school day, "how long does this student get"
     * is better answered by leaving the practice untimed. It is deliberately
     * well clear of any real exam — a three-hour paper fits with room to spare —
     * so the limit is never the thing a tutor has to work around.
     */
    MAX_PRACTICE_DURATION_MINUTES: 480,
    /**
     * How many digits a sign-in code has (V1.md section 6).
     *
     * Shared because both ends need it and for different reasons: the API
     * configures Better Auth's generator with it, and the browser uses it for
     * the field's `maxLength` and for the "have you finished typing" check
     * before a request is worth making. Two copies of a six would be two copies
     * that can disagree, and the way they would disagree is a form that refuses
     * a code the server just issued.
     *
     * Six is what every other product puts in this box, which matters: a person
     * reading digits off a notification is pattern-matching, not counting.
     */
    SIGN_IN_CODE_LENGTH: 6,
};
//# sourceMappingURL=limits.js.map